import Vue from 'vue'
const _ = require('lodash')
import {SmartPlugin, SmartPluginContainer} from '../smart'

export class Breakpoint {
    constructor(manager, name, minWidth, maxWidth) {
        this.name = name;
        this.minWidth = minWidth;
        this.maxWidth = maxWidth;
        this.$manager = manager;

        if (this.minWidth && this.maxWidth) {
            this.mediaQueries = {
                exact: `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`,
                notExact: `not (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`,
                exactOrBelow: `(max-width: ${maxWidth }px)`,
                exactOrAbove: `(min-width: ${minWidth}px)`,
                above: `(min-width: ${maxWidth + 1}px)`,
                below: `(max-width: ${minWidth - 1}px)`
            }
        } else if (this.maxWidth) {
            this.mediaQueries = {
                exact: `(max-width: ${maxWidth}px)`,
                notExact: `(min-width: ${maxWidth + 1}px)`,
                above: `(min-width: ${maxWidth + 1}px)`,
            }
        } else if (this.minWidth) {
            this.mediaQueries = {
                exact: `(min-width: ${minWidth}px)`,
                notExact: `(max-width: ${minWidth - 1}px)`,
                below: `(max-width: ${minWidth - 1}px)`
            }
        }

        this.mediaQueries.notExactOrBelow = this.mediaQueries.above;
        this.mediaQueries.notExactOrAbove = this.mediaQueries.below;
        this.mediaQueries.notBelow = this.mediaQueries.exactOrAbove;
        this.mediaQueries.notAbove = this.mediaQueries.exactOrBelow;
    }

    get isAbove() {
        var width = this.$manager.currentWidth;
        return this.maxWidth && width > this.maxWidth;
    }
    get isBelow() {
        var width = this.$manager.currentWidth;
        return this.minWidth && width < this.minWidth;
    }
    get isCurrent() {
        var width = this.$manager.currentWidth;
        if((this.minWidth && width < this.minWidth) || (this.maxWidth && width > this.maxWidth))
            return false;
        return true;
    }
    get isCurrentOrAbove() {
        var width = this.$manager.currentWidth;
        return this.minWidth && width >= this.minWidth;
    }
    get isCurrentOrBelow() {
        var width = this.$manager.currentWidth;
        return this.maxWidth && width <= this.maxWidth;
    }
}

export class BreakpointPlugin extends SmartPlugin{
    constructor(name, props){
        super(name, {...props, manager:{required:true}});
    }
}

export class BreakpointManager extends SmartPluginContainer{
    constructor(props, inject){
        super('breakpoint-manager', {...props, breakpoints:{default: null}}, {manager(v){return v;}, smart(v){v.$options.smart}, ...inject});
        this.$observers = Vue.observable({
            currentWidth: 0,
            isPrintMode: false,
            printSize: null,
        });

        var self = this;
        this.onResize = _.throttle(function(){self.recalculateBreakpoints()}, 100);
        this.$jssScript = null;
    }

    initialize(props){
        super.initialize(props);
        var self = this;
        this.$breakpoints = Object.entries(this.$options.breakpoints || this.defaultBreakpoints()).reduce((cur, [name, value])=>{cur[name] = new Breakpoint(self, name, value.minWidth, value.maxWidth); return cur}, {});
        this.$observers.printSize = this.$options.printSize;

        window.addEventListener("resize", this.onResize);
        if(window.matchMedia('print').addEventListener)
            window.matchMedia('print').addEventListener('change', this.onPrintChange);

        this.recalculateBreakpoints();

        if(this.$options.plugins){
            this.$options.plugins.forEach(p=>self.use(p));
        }

        this.generateJss();
    }

    get breakpoints() {
        return this.$breakpoints;
    }

    get currentWidth() {
        return this.$observers.currentWidth;
    }

    get classes() {
        var classArray = Object.values(this.$breakpoints).map((bp) => bp.isBelow ? `below-${bp.name}` : (bp.isAbove ? `above-${bp.name}` : bp.name));
        classArray.push('smart-root')
        if (this.$observers.isPrintMode)
            classArray.push('print');
        return classArray;
    }
    
    get current() {
        return Object.values(this.$breakpoints).find(bp => bp.isCurrent);
    }

    recalculateBreakpoints(){
        this.$observers.currentWidth = this.$observers.isPrintMode && this.$observers.printSize ? this.$observers.printSize : window.innerWidth;
    }

    onPrintChange(e) {
        this.$observers.isPrintMode = e.matches;
        if(this.$observers.isPrintMode)
            this.recalculateBreakpoints();
    }

    generateJss(){
        var mq = this.$options.smart.$plugins['mediaquery-manager'];

        Object.values(this.$breakpoints).forEach(bp => {
            var bpName = bp.name;

            var bpMq = Object.entries(bp.mediaQueries).reduce((cur, [name, value])=>{
                if(value)
                    cur[name] = mq.ensureQuery(value, [`${bpName}-${name}`]);
                return cur;
            }, {});

            const applyChunks = function(chunks){
                Object.entries(chunks).forEach(([key, value])=>{
                    var mq = bpMq[key];
                    if(mq)
                        mq.chunks.push(value);
                })
            }

            const displayNone = {display: 'none'}
            applyChunks({
                exact:{[`hide-${bpName}`]: displayNone},
                exactOrAbove: {[`hide-${bpName}-and-above`]: displayNone},
                exactOrBelow: {[`hide-${bpName}-and-below`]: displayNone},
                above: {[`hide-above-${bpName}`]: displayNone},
                below: {[`hide-below-${bpName}`]: displayNone},

                notExact:{[`only-${bpName}`]: displayNone},
                notExactOrAbove: {[`only-${bpName}-and-above`]: displayNone},
                notExactOrBelow: {[`only-${bpName}-and-below`]: displayNone},
                notAbove: {[`only-above-${bpName}`]: displayNone},
                notBelow: {[`only-below-${bpName}`]: displayNone}
            })
            Object.values(this.$plugins).forEach(plugin => {
                var pChunks = null;
                if(plugin.extendJss)
                    pChunks = plugin.extendJss(bp);
                else if(plugin.plugin && plugin.plugin.extendJss)
                    pChunks = plugin.extendJss(bp, plugin.options);
                
                if(pChunks)
                    applyChunks(pChunks);
            });
            mq.rebuild();
        });

        mq.rebuild();
    }

    defaultBreakpoints(){
        return {
            xl:{ minWidth: 1920},
            lg:{ minWidth: 1280, maxWidth: 1919},
            md:{ minWidth: 960, maxWidth: 1279},
            sm:{ minWidth: 600, maxWidth: 959},
            xs:{ maxWidth: 599},
        }
    }

    inject(props){
        super.inject(props);
        props.manager = this;
    }
}